import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Spinner, Container, Row, Col } from 'reactstrap';
import { useTranslator, useToast, callNativeMobileApp } from '@front-activerse/common-components';
import {
  WearableConnectionsConnectorApollo,
  WearableProvidersConnectorApollo,
  InitiateWearableConnectionConnectorApollo,
  DisconnectWearableConnectorApollo,
  ConfirmConnectionConnectorApollo,
  TriggerSyncConnectorApollo,
} from '@front-activerse/common-components';
import type { WearableMutationResult } from '@front-activerse/common-components';
import Layout from '../Layout/Layout';
import TitleBar from '../Layout/TitleBar';
import type { WearableConnection, WearableProvider } from './types';
import ProviderCard from './ProviderCard';

interface Props {
  redirectUri: string;
}

// Cross-platform contract: the native shell injects this global into the web view
// with the app's custom URL scheme, used to build app-scheme return links that bring
// the user back into the app from external services (e.g. the wearables OAuth
// `<scheme>://wearables/callback` redirect). The exact value differs per platform —
// iOS uses the bundle id (ACVWebViewReact.swift, `addUserScript`); Android uses the
// bundle id plus an `.oauth` suffix to avoid colliding with its general deep-link
// scheme (WebViewExtensions.injectAppScheme) — so treat it as an opaque scheme, not a
// bundle id. Keep the name in sync across all three repos if it ever changes. Its
// presence is the signal to hand the connect flow off to native instead of doing a
// full-page redirect, which inside the in-app webview would eject to external Safari
// and never return.
declare global {
  interface Window {
    __ACV_APP_SCHEME__?: string;
  }
}

const getNativeAppScheme = (): string | undefined => window.__ACV_APP_SCHEME__;

interface InnerProps {
  providers: WearableProvider[];
  providersLoading: boolean;
  providersError: boolean;
  connections: WearableConnection[];
  loading: boolean;
  initiateConnection: (
    providerKey: string,
    redirectUri: string
  ) => Promise<{ code: string; authorizationUrl?: string } | null>;
  connecting: boolean;
  disconnectProvider: (providerKey: string) => Promise<WearableMutationResult>;
  disconnecting: boolean;
  confirmConnection: (providerKey: string) => Promise<WearableMutationResult>;
  triggerSync: (providerKey: string) => Promise<WearableMutationResult>;
  syncing: boolean;
  redirectUri: string;
}

const WearablesInner: React.FC<InnerProps> = ({
  providers,
  providersLoading,
  providersError,
  connections,
  loading,
  initiateConnection,
  connecting,
  disconnectProvider,
  disconnecting,
  confirmConnection,
  triggerSync,
  syncing,
  redirectUri,
}) => {
  const { t } = useTranslator();
  const { toastError } = useToast();
  const { search } = useLocation();
  const [confirmingProviders, setConfirmingProviders] = useState<Set<string>>(new Set());
  // Guards against confirming the same provider twice: the web flow remounts with
  // ?confirm=… while the native flow navigates here in-SPA (no remount), so we react
  // to the query string rather than mount, and dedupe by provider.
  const confirmedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const providerToConfirm = new URLSearchParams(search).get('confirm');

    if (!providerToConfirm || confirmedRef.current.has(providerToConfirm)) return;
    confirmedRef.current.add(providerToConfirm);

    window.history.replaceState(null, '', window.location.pathname);

    setConfirmingProviders((prev) => new Set([...prev, providerToConfirm]));
    confirmConnection(providerToConfirm)
      .then((result) => {
        if (!result.ok) toastError();
      })
      .finally(() => {
        setConfirmingProviders((prev) => {
          const next = new Set(prev);
          next.delete(providerToConfirm);
          return next;
        });
      });
    // Re-runs when the SPA URL gains ?confirm=… (native flow navigates in-place);
    // confirmConnection/toastError are stable enough and guarded by confirmedRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const connectionMap = connections.reduce<Record<string, WearableConnection>>((acc, conn) => {
    acc[conn.provider.openWearablesProvider] = conn;
    return acc;
  }, {});

  const handleConnect = async (providerKey: string) => {
    const nativeScheme = getNativeAppScheme();

    // Native (in-app webview): run OAuth through the system auth session. The
    // callback uses the app URL scheme so the backend's final redirect returns
    // into the app; native then navigates back to /wearables?confirm=… to
    // confirm the connection.
    if (nativeScheme) {
      const callbackUri = `${nativeScheme}://wearables/callback?confirm=${providerKey}`;
      const result = await initiateConnection(providerKey, callbackUri);
      if (result?.authorizationUrl) {
        callNativeMobileApp({
          action: 'open_oauth',
          payload: { url: result.authorizationUrl, provider: providerKey },
        });
      }
      return;
    }

    // Web: full-page redirect to the provider, returning to /wearables?confirm=…
    const callbackUri = `${redirectUri}?confirm=${providerKey}`;
    const result = await initiateConnection(providerKey, callbackUri);
    if (result?.authorizationUrl) {
      window.location.href = result.authorizationUrl;
    }
  };

  const handleDisconnect = async (providerKey: string) => {
    const result = await disconnectProvider(providerKey);
    if (!result.ok) toastError();
  };

  const handleSync = async (providerKey: string) => {
    const result = await triggerSync(providerKey);
    if (!result.ok) toastError();
  };

  return (
    <Layout>
      <TitleBar showBackBtn={false}>{t('wearables.navigation_title')}</TitleBar>
      <section className="page-template-wearable-integrations full-height3a">
        <Container className="py-xl">
          <Row>
            <Col md={{ size: 8, offset: 2 }}>
              {loading || providersLoading ? (
                <div className="d-flex justify-content-center py-xxl">
                  <Spinner />
                </div>
              ) : providersError ? (
                <div
                  className="text-center py-xxl text-text-variant-color"
                  data-testid="wearables-catalog-error"
                >
                  {t('wearables.catalog.error')}
                </div>
              ) : providers.length === 0 ? (
                <div
                  className="text-center py-xxl text-text-variant-color"
                  data-testid="wearables-catalog-empty"
                >
                  {t('wearables.catalog.empty')}
                </div>
              ) : (
                providers.map((provider) => (
                  <ProviderCard
                    key={provider.openWearablesProvider}
                    provider={provider}
                    connection={connectionMap[provider.openWearablesProvider]}
                    onConnect={handleConnect}
                    onDisconnect={handleDisconnect}
                    onSync={handleSync}
                    connecting={connecting}
                    disconnecting={disconnecting}
                    syncing={syncing}
                    isConfirming={confirmingProviders.has(provider.openWearablesProvider)}
                  />
                ))
              )}
            </Col>
          </Row>
        </Container>
      </section>
    </Layout>
  );
};

const WearablesContainer: React.FC<Props> = ({ redirectUri }) => {
  return (
    <WearableProvidersConnectorApollo>
      {({ providers, loading: providersLoading, error: providersError }) => (
        <WearableConnectionsConnectorApollo>
          {({ loading, connections }) => (
            <InitiateWearableConnectionConnectorApollo>
              {({ initiateConnection, loading: connecting }) => (
                <DisconnectWearableConnectorApollo>
                  {({ disconnectProvider, loading: disconnecting }) => (
                    <ConfirmConnectionConnectorApollo>
                      {({ confirmConnection }) => (
                        <TriggerSyncConnectorApollo>
                          {({ triggerSync, loading: syncing }) => (
                            <WearablesInner
                              providers={providers}
                              providersLoading={providersLoading}
                              providersError={providersError}
                              connections={connections}
                              loading={loading}
                              initiateConnection={initiateConnection}
                              connecting={connecting}
                              disconnectProvider={disconnectProvider}
                              disconnecting={disconnecting}
                              confirmConnection={confirmConnection}
                              triggerSync={triggerSync}
                              syncing={syncing}
                              redirectUri={redirectUri}
                            />
                          )}
                        </TriggerSyncConnectorApollo>
                      )}
                    </ConfirmConnectionConnectorApollo>
                  )}
                </DisconnectWearableConnectorApollo>
              )}
            </InitiateWearableConnectionConnectorApollo>
          )}
        </WearableConnectionsConnectorApollo>
      )}
    </WearableProvidersConnectorApollo>
  );
};

export default WearablesContainer;
